import { useEffect, useMemo, useState } from 'react';

import { getView } from '../../engine/prism.js';
import { setOverlay } from '../../engine/host.js';
import type { EntityDesc } from '../../lib/scene-doc.js';
import { docIO } from '../../lib/doc-io.js';
import { logEditor } from '../../state/console.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useScene } from '../../state/scene.js';
import { useWorkspace } from '../../state/workspace.js';
import { ColorField, IconButton, NumberField, PropRow, SelectField, TextField, Toggle, VecField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import {
  newTerrainJson,
  newVoxelWorldJson,
  newWorldSeed,
  serializeTerrain,
  terrainCommand,
  useTerrain,
  voxelWorldCreateOp,
  voxelWorldInfo,
  voxelWorldLayers,
  type TerrainDetailJson,
  type TerrainJson,
  type TerrainLayerJson,
  type TerrainTool,
} from './terrain-store.js';
import { BrushShapePanel, NeighborsPanel, StampPanel, TreesPanel, VoxelBrushPanel, VoxelMaterialPalette } from './TerrainToolPanels.js';
import {
  createNeighborCommands,
  findTreeGroup,
  neighborGrid,
  neighborPath,
  neighborsOf,
  neighborTerrainJson,
  OPPOSITE,
  type Side,
} from './terrain-tools.js';

/**
 * Terrain tools for a selected `terrain` component: sculpt / paint brushes
 * (applied by the engine in the scene view, so strokes and the AI's
 * `terrainEdit` commands go through the same code), heightmap import and
 * resize, and the data file's layers and details.
 */

interface ToolDef {
  tool: TerrainTool;
  label: string;
  title: string;
  /** Label and title on voxel terrains (unset = heightmap only). */
  voxel?: { label: string; title: string };
}

const TOOLS: ToolDef[] = [
  { tool: 'raise', label: 'Raise', title: 'Raise terrain (Shift lowers)', voxel: { label: 'Add', title: 'Add the selected material (Shift digs)' } },
  { tool: 'lower', label: 'Lower', title: 'Lower terrain (Shift raises)', voxel: { label: 'Dig', title: 'Dig material away (Shift adds the selected material)' } },
  { tool: 'smooth', label: 'Smooth', title: 'Smooth bumps', voxel: { label: 'Smooth', title: 'Smooth the surface: rounds edges and evens out bumps and pits' } },
  {
    tool: 'flatten',
    label: 'Flatten',
    title: 'Flatten to the height where the stroke starts',
    voxel: { label: 'Flatten', title: 'Flatten to the plane through the point where the stroke starts, facing the surface there (fills with the selected material)' },
  },
  { tool: 'sharpen', label: 'Sharpen', title: 'Sharpen peaks: raise peaks and ridges, deepen valleys' },
  { tool: 'stamp', label: 'Stamp', title: 'Place a height stamp per click (Shift inverts)' },
  { tool: 'paint', label: 'Paint', title: 'Paint the selected layer', voxel: { label: 'Paint', title: 'Set the material of the surface under the brush' } },
  { tool: 'detail', label: 'Details', title: 'Paint the selected detail layer (Shift erases)' },
  { tool: 'holes', label: 'Holes', title: 'Paint holes in the terrain (Shift fills them again)' },
  { tool: 'trees', label: 'Trees', title: 'Paint tree instances (prefabs / models; Shift erases)' },
];

/** The tools of a voxel terrain, with their voxel labels. */
const VOXEL_TOOLS = TOOLS.flatMap((t) => (t.voxel ? [{ tool: t.tool, ...t.voxel }] : []));

/** Tools whose dabs take the brush shape (falloff, mask, spacing). */
const SHAPED: ReadonlySet<TerrainTool> = new Set<TerrainTool>(['raise', 'lower', 'smooth', 'flatten', 'sharpen', 'paint', 'detail', 'holes']);

/** Voxel tools that use the selected material, and what for. */
const MATERIAL_USE: Partial<Record<TerrainTool, string>> = {
  raise: 'added by the brush',
  lower: 'added with Shift',
  flatten: 'fills up to the plane',
  paint: 'painted on the surface',
};

const RESOLUTIONS = ['33', '65', '129', '257', '513', '1025', '2049', '4097'];

/** Project files ending in one of `suffixes` ('|'-separated). */
function useFilesEnding(suffixes: string): string[] {
  const entries = useProjectFiles((s) => s.entries);
  return useMemo(() => {
    const list = suffixes.split('|');
    return entries.filter((e) => e.kind !== 'directory' && list.some((x) => e.path.endsWith(x))).map((e) => e.path);
  }, [entries, suffixes]);
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'terrain';
}

export function TerrainEditor({
  entity,
  value,
  onChange,
}: {
  entity: EntityDesc;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const path = typeof value.data === 'string' ? value.data : '';
  const projectId = useWorkspace((s) => s.projectId);
  const file = useTerrain((s) => (path ? s.files[path] : undefined));
  const painting = useTerrain((s) => s.painting === entity.id);
  const brush = useTerrain((s) => s.brush);
  const { load, setJson, undo, redo, setBrush } = useTerrain.getState();
  const materials = useFilesEnding('.material.json');
  const physicsMaterials = useFilesEnding('.physicsmat.json');
  const meshes = useFilesEnding('.model.json|.glb|.gltf');
  const images = useFilesEnding('.png');
  const [image, setImage] = useState('');
  const [resize, setResize] = useState<{ size?: [number, number]; resolution?: number; heightScale?: number }>({});
  const doc = useScene((s) => s.doc);
  const entries = useProjectFiles((s) => s.entries);
  const treeGroup = useMemo(() => (doc ? findTreeGroup(doc, entity.id) : null), [doc, entity.id]);

  // The engine's tree brush adds to / erases from this terrain's tree group.
  useEffect(() => {
    if (!painting) return;
    const b = useTerrain.getState().brush;
    if ((b.trees.group ?? null) !== treeGroup) setBrush({ trees: { ...b.trees, group: treeGroup } });
  }, [painting, treeGroup, setBrush]);

  useEffect(() => {
    if (path) void load(path);
  }, [path, load]);

  // Stop sculpting when this terrain is deselected.
  useEffect(
    () => () => {
      if (useTerrain.getState().painting === entity.id) useTerrain.getState().setBrush({ painting: null });
    },
    [entity.id],
  );

  /** Writes a new data file (flat heightmap or generated voxel world) and points the component at it. */
  const createData = async (voxelWorld: boolean) => {
    if (!projectId) return;
    const target = `terrain/${slug(entity.id)}.terrain.json`;
    try {
      await docIO().write(projectId, target, serializeTerrain(voxelWorld ? newVoxelWorldJson(newWorldSeed()) : newTerrainJson()));
      onChange({ ...value, data: target });
    } catch (e) {
      logEditor('error', `create ${target}: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  if (!path) {
    return (
      <div className="tilemap-editor">
        <p className="muted tilemap-hint">This terrain has no data file: it is a flat placeholder and edits are not saved.</p>
        <button type="button" className="small primary" onClick={() => void createData(false)}>
          <Icon name="plus" size={12} /> Create terrain data
        </button>
        <button
          type="button"
          className="small ghost"
          title="A generated voxel world you can dig, tunnel and build in: hills, biomes, soil, rock, caves, coal and a bedrock floor"
          onClick={() => void createData(true)}
        >
          <Icon name="plus" size={12} /> Create voxel world
        </button>
      </div>
    );
  }
  if (!file) return <p className="muted tilemap-hint">Loading {path}…</p>;
  if (!file.json) return <p className="inline-error">{path}: {file.error ?? 'cannot be read'}</p>;

  const json = file.json;
  const voxel = json.type === 'voxel';
  const layers: TerrainLayerJson[] = Array.isArray(json.layers) ? json.layers : [];
  const details: TerrainDetailJson[] = Array.isArray(json.details) ? json.details : [];
  const update = (patch: Partial<TerrainJson>) => setJson(path, { ...json, ...patch });
  const setLayer = (i: number, patch: Partial<TerrainLayerJson>) =>
    update({ layers: layers.map((l, j) => (j === i ? clean({ ...l, ...patch }) : l)) });
  const setDetail = (i: number, patch: Partial<TerrainDetailJson>) =>
    update({ details: details.map((d, j) => (j === i ? clean({ ...d, ...patch }) : d)) });
  const tools = voxel ? VOXEL_TOOLS : TOOLS;
  const voxelTool = VOXEL_TOOLS.some((t) => t.tool === brush.tool);
  const info = voxel ? voxelWorldInfo(json) : null;
  const size = json.size ?? [128, 128];
  const linked = neighborsOf(value);
  const grid = doc && !voxel ? neighborGrid(doc, entity.id, size) : [];

  /**
   * Replaces the terrain with a new generated voxel world: the layers the
   * generator names go in through the file first (`create` keeps the
   * layers), then the `create` op (two undo steps).
   */
  const createVoxelWorld = async () => {
    const view = getView('scene');
    if (!view) {
      logEditor('warn', 'terrain: the scene view is not running');
      return;
    }
    if (!window.confirm('Replace this terrain with a new generated voxel world? Its layers are replaced too. (Undo restores it.)')) return;
    setJson(path, { ...json, layers: voxelWorldLayers() });
    await view.fileChanged(path);
    terrainCommand(entity.id, voxelWorldCreateOp(newWorldSeed()));
  };

  /** Creates the tile on `side`: data file, entity + links (one undo step), then seams it to this tile. */
  const createNeighbor = async (side: Side) => {
    const view = getView('scene');
    if (!projectId || !doc) return;
    const target = neighborPath(path, side, (p) => entries.some((e) => e.path === p));
    const plan = createNeighborCommands(doc, entity.id, side, size, target);
    if ('error' in plan) {
      logEditor('warn', `terrain: ${plan.error}`);
      return;
    }
    try {
      const text = serializeTerrain(neighborTerrainJson(json));
      setOverlay(target, text);
      await docIO().write(projectId, target, text);
      await view?.fileChanged(target);
    } catch (e) {
      logEditor('error', `create ${target}: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    const result = useScene.getState().applyBatch(plan.commands);
    if (!result?.ok) return;
    useScene.getState().select([entity.id]);
    // Copy this tile's border into the new one (eased over 16 samples) once the engine has loaded it.
    for (let attempt = 0; attempt < 20; attempt += 1) {
      await new Promise((r) => setTimeout(r, 150));
      const outcome = await getView('scene')?.command({
        cmd: 'terrainEdit',
        id: plan.newId,
        edit: { op: 'stitch', side: OPPOSITE[side], neighbor: entity.id, mode: 'copy', width: 16 },
      });
      if (!outcome || outcome.ok) return;
      if (!/no loaded terrain/.test(outcome.error ?? '')) {
        logEditor('warn', `terrain: ${outcome.error ?? 'stitch failed'}`);
        return;
      }
    }
  };

  return (
    <div className="tilemap-editor">
      <div className="tilemap-row">
        <span className="muted mono" title={path}>
          {voxel
            ? `voxel · ${json.chunkSize ?? 32}³ chunks · ${json.voxelSize ?? 1} m`
            : `${size[0]} × ${size[1]} m · ${json.resolution ?? 129}² · ${json.heightScale ?? 50} m`}
        </span>
        <span className="bar-spacer" />
        <IconButton icon="undo" title="Undo terrain edit" size={12} disabled={file.past.length === 0} onClick={() => undo(path)} />
        <IconButton icon="redo" title="Redo terrain edit" size={12} disabled={file.future.length === 0} onClick={() => redo(path)} />
      </div>

      <div className="tile-palette" role="toolbar" aria-label="Terrain tools">
        {tools.map((t) => (
          <button
            key={t.tool}
            type="button"
            className={`small ${painting && brush.tool === t.tool ? 'primary' : 'ghost'}`}
            title={t.title}
            onClick={() =>
              painting && brush.tool === t.tool
                ? setBrush({ painting: null })
                : // Voxel strength is a 0..1 rate (the heightmap raise default is 2 m/s).
                  setBrush({ tool: t.tool, painting: entity.id, ...(voxel && brush.strength > 1 ? { strength: 1 } : {}) })
            }
          >
            {t.label}
          </button>
        ))}
      </div>
      {painting && (
        <p className="muted tilemap-hint">
          Drag on the terrain in the scene view{voxel ? ' (Shift swaps Add and Dig)' : ''}. Each stroke is saved to {path}.
        </p>
      )}
      {brush.tool === 'stamp' && !voxel ? (
        <StampPanel stamp={brush.stamp} onChange={(stamp) => setBrush({ stamp })} />
      ) : voxel ? (
        <>
          <PropRow label="Radius" hint="Brush radius in meters (box: half its size)">
            <NumberField value={brush.radius} step={0.25} min={0.1} onChange={(radius) => setBrush({ radius })} />
          </PropRow>
          <PropRow label="Strength" hint="0..1: share of the full effect applied per quarter second (1 = full effect in about 0.25 s)">
            <NumberField value={brush.strength} step={0.05} min={0} max={1} onChange={(strength) => setBrush({ strength })} />
          </PropRow>
        </>
      ) : (
        <>
          <PropRow label="Radius" hint="Brush radius in meters">
            <NumberField value={brush.radius} step={0.5} min={0.1} onChange={(radius) => setBrush({ radius })} />
          </PropRow>
          {brush.tool !== 'holes' && brush.tool !== 'trees' && (
            <PropRow label="Strength" hint="Raise / lower: meters per second. Others: blend rate (0..1)">
              <NumberField value={brush.strength} step={0.1} min={0} onChange={(strength) => setBrush({ strength })} />
            </PropRow>
          )}
        </>
      )}
      {brush.tool === 'holes' && !voxel && <p className="muted tilemap-hint">Holes have no surface, collider or ground. Shift-drag fills them again.</p>}
      {SHAPED.has(brush.tool) && !voxel && <BrushShapePanel brush={brush} setBrush={setBrush} />}
      {brush.tool === 'trees' && !voxel && <TreesPanel trees={brush.trees} onChange={(trees) => setBrush({ trees })} />}
      {voxel && voxelTool && <VoxelBrushPanel brush={brush} setBrush={setBrush} />}
      {voxel && MATERIAL_USE[brush.tool] && (
        <>
          <div className="tilemap-row">
            <span className="muted">Material</span>
            <span className="bar-spacer" />
            <span className="muted">{MATERIAL_USE[brush.tool]}</span>
          </div>
          {layers.length > 0 ? (
            <VoxelMaterialPalette layers={layers} selected={brush.layer} onSelect={(layer) => setBrush({ layer })} />
          ) : (
            <p className="muted tilemap-hint">Add a layer below to pick a material (material 1 = the first layer).</p>
          )}
        </>
      )}
      {!voxel && brush.tool === 'paint' && layers.length > 0 && (
        <PropRow label="Layer" hint="Layer to paint">
          <SelectField
            value={String(Math.min(brush.layer, layers.length - 1))}
            options={layers.map((l, i) => ({ value: String(i), label: l.name || `layer ${i}` }))}
            onChange={(v) => setBrush({ layer: Number(v) })}
          />
        </PropRow>
      )}
      {brush.tool === 'detail' && details.length > 0 && (
        <PropRow label="Detail" hint="Detail layer to paint">
          <SelectField
            value={String(Math.min(brush.detail, details.length - 1))}
            options={details.map((d, i) => ({ value: String(i), label: d.name || `detail ${i}` }))}
            onChange={(v) => setBrush({ detail: Number(v) })}
          />
        </PropRow>
      )}

      {info && (
        <>
          <div className="tilemap-row">
            <span className="muted">Voxel world</span>
          </div>
          <PropRow label="Chunks" hint="Voxels per chunk side · meters per voxel">
            <span className="mono">
              {info.chunkSize}³ · {info.voxelSize} m
            </span>
          </PropRow>
          <PropRow label="Edited chunks" hint="Chunks stored in the data file (with a generator only the edited ones, saved as differences)">
            <span className="mono">{info.editedChunks}</span>
          </PropRow>
          <PropRow label="Generator" hint="prism.voxelGen/1: generates every chunk that is not stored (none = missing chunks are air)">
            <span className="mono" title={info.generator.kind === 'file' ? info.generator.path : undefined}>
              {info.generator.kind === 'none' ? 'none' : info.generator.kind === 'file' ? info.generator.path : `seed ${info.generator.seed}`}
            </span>
          </PropRow>
          {info.bounds && (
            <PropRow label="Bounds" hint={`Clip box (local meters, outside is air): ${info.bounds.min.join(', ')} to ${info.bounds.max.join(', ')}`}>
              <span className="mono">{info.bounds.max.map((v, k) => v - info.bounds!.min[k]!).join(' × ')} m</span>
            </PropRow>
          )}
        </>
      )}

      {!voxel && (
        <>
          <div className="tilemap-row">
            <span className="muted">Heightmap</span>
          </div>
          <PropRow label="Import" hint="Grayscale PNG (8 or 16 bit) copied into the heights">
            <TextField value={image} onChange={setImage} placeholder="terrain/height.png" mono list="terrain-pngs" />
            <datalist id="terrain-pngs">
              {images.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <button type="button" className="small ghost" disabled={!image} onClick={() => terrainCommand(entity.id, { op: 'importHeightmap', image })}>
              Import
            </button>
          </PropRow>
          <PropRow label="Size" hint="Meters along X and Z">
            <VecField size={2} step={1} labels={['X', 'Z']} value={resize.size ?? size} onChange={(v) => setResize({ ...resize, size: [v[0] ?? 1, v[1] ?? 1] })} />
          </PropRow>
          <PropRow label="Resolution" hint="Height samples per side">
            <SelectField
              value={String(resize.resolution ?? json.resolution ?? 129)}
              options={RESOLUTIONS}
              onChange={(v) => setResize({ ...resize, resolution: Number(v) })}
            />
          </PropRow>
          <PropRow label="Height scale" hint="Meters of the highest possible point">
            <NumberField value={resize.heightScale ?? json.heightScale ?? 50} step={1} min={0.1} onChange={(heightScale) => setResize({ ...resize, heightScale })} />
          </PropRow>
          {Object.keys(resize).length > 0 && (
            <div className="tilemap-row">
              <span className="bar-spacer" />
              <button type="button" className="small ghost" onClick={() => setResize({})}>
                Cancel
              </button>
              <button
                type="button"
                className="small primary"
                onClick={() => void terrainCommand(entity.id, { op: 'resize', ...resize }).then((ok) => ok && setResize({}))}
              >
                Apply size
              </button>
            </div>
          )}
        </>
      )}

      <div className="tilemap-row">
        <span className="muted">Layers</span>
        <span className="bar-spacer" />
        <button
          type="button"
          className="ghost small"
          onClick={() => update({ layers: [...layers, { name: `Layer ${layers.length + 1}`, tileSize: [8, 8], color: [1, 1, 1] }] })}
        >
          <Icon name="plus" size={12} /> Layer
        </button>
      </div>
      {layers.length === 0 && <p className="muted tilemap-hint">No layers: the terrain uses the component's material.</p>}
      {layers.map((l, i) => (
        <div key={i} className="tilemap-layers">
          <div className="tilemap-layer">
            <span className="muted mono">{voxel ? i + 1 : i}</span>
            <input className="tilemap-layer-name" value={l.name ?? ''} onChange={(e) => setLayer(i, { name: e.target.value })} aria-label="Layer name" />
            <IconButton icon="trash" title="Delete layer (its paint goes to the first layer)" size={12} onClick={() => update({ layers: layers.filter((_, j) => j !== i) })} />
          </div>
          <PropRow label="Material" hint="*.material.json (empty = the terrain component's material)">
            <TextField
              value={typeof l.material === 'string' ? l.material : ''}
              onChange={(v) => setLayer(i, { material: v || undefined })}
              placeholder="materials/grass.material.json"
              mono
              list="terrain-materials"
            />
          </PropRow>
          <PropRow label="Tile size" hint="Meters per texture repeat (X, Z)">
            <VecField size={2} step={0.5} labels={['X', 'Z']} value={l.tileSize ?? [8, 8]} onChange={(v) => setLayer(i, { tileSize: [v[0] ?? 8, v[1] ?? 8] })} />
          </PropRow>
          <PropRow label="Tint" hint="Vertex color blended smoothly across layer borders">
            <ColorField value={l.color ?? [1, 1, 1]} onChange={(v) => setLayer(i, { color: [v[0] ?? 1, v[1] ?? 1, v[2] ?? 1] })} />
          </PropRow>
          <PropRow label="Physics material" hint="*.physicsmat.json used where this layer dominates (empty = the terrain component's physicsMaterial)">
            {l.physicsMaterial !== undefined && typeof l.physicsMaterial !== 'string' ? (
              <span className="muted">inline material (edit the data file)</span>
            ) : (
              <TextField
                value={typeof l.physicsMaterial === 'string' ? l.physicsMaterial : ''}
                onChange={(v) => setLayer(i, { physicsMaterial: v || undefined })}
                placeholder="physics/grass.physicsmat.json"
                mono
                list="terrain-physics-materials"
              />
            )}
          </PropRow>
          {voxel && (
            <>
              <PropRow label="Side layer" hint="Layer shown instead of this one on steep faces (grass tops with dirt sides)">
                <SelectField
                  value={l.sideLayer ?? ''}
                  options={[{ value: '', label: '(none)' }, ...layers.filter((o, j) => j !== i && o.name).map((o) => ({ value: o.name!, label: o.name! }))]}
                  onChange={(v) => setLayer(i, { sideLayer: v || undefined })}
                />
              </PropRow>
              <PropRow label="Diggable" hint="Off: digging and mining never remove this layer (bedrock)">
                <Toggle checked={l.diggable !== false} onChange={(on) => setLayer(i, { diggable: on ? undefined : false })} />
              </PropRow>
            </>
          )}
        </div>
      ))}
      <datalist id="terrain-materials">
        {materials.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
      <datalist id="terrain-physics-materials">
        {physicsMaterials.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>

      {!voxel && (
        <>
          <div className="tilemap-row">
            <span className="muted">Details</span>
            <span className="bar-spacer" />
            <button
              type="button"
              className="ghost small"
              onClick={() => update({ details: [...details, { name: `Detail ${details.length + 1}`, mesh: '', density: 2, scale: [0.8, 1.2], maxSlope: 35 }] })}
            >
              <Icon name="plus" size={12} /> Detail
            </button>
          </div>
          {details.map((d, i) => (
            <div key={i} className="tilemap-layers">
              <div className="tilemap-layer">
                <input className="tilemap-layer-name" value={d.name ?? ''} onChange={(e) => setDetail(i, { name: e.target.value })} aria-label="Detail name" />
                <IconButton icon="trash" title="Delete detail" size={12} onClick={() => update({ details: details.filter((_, j) => j !== i) })} />
              </div>
              <PropRow label="Mesh" hint="Model, glTF or primitive (primitive:grass / primitive:tallGrass: wind-swept grass clumps)">
                <TextField value={d.mesh ?? ''} onChange={(v) => setDetail(i, { mesh: v })} placeholder="models/rock.model.json" mono list="terrain-meshes" />
              </PropRow>
              <PropRow label="Density" hint="Instances per 100 m² where painted / allowed">
                <NumberField value={d.density ?? 1} step={0.5} min={0} max={400} onChange={(density) => setDetail(i, { density })} />
              </PropRow>
              <PropRow label="Scale" hint="Random uniform scale range">
                <VecField size={2} step={0.05} labels={['min', 'max']} value={d.scale ?? [0.8, 1.2]} onChange={(v) => setDetail(i, { scale: [v[0] ?? 1, v[1] ?? 1] })} />
              </PropRow>
              <PropRow label="Max slope" hint="Degrees">
                <NumberField value={d.maxSlope ?? 35} step={1} min={0} max={90} onChange={(maxSlope) => setDetail(i, { maxSlope })} />
              </PropRow>
              <PropRow label="Only on layer" hint="Density × that layer's painted weight">
                <SelectField
                  value={d.layer ?? ''}
                  options={[{ value: '', label: '(anywhere)' }, ...layers.map((l) => ({ value: l.name ?? '', label: l.name ?? '' }))]}
                  onChange={(v) => setDetail(i, { layer: v || undefined })}
                />
              </PropRow>
              <PropRow label="Align to ground" hint="Tilt instances to the ground normal">
                <Toggle checked={d.alignToNormal === true} onChange={(alignToNormal) => setDetail(i, { alignToNormal })} />
              </PropRow>
              <PropRow label="Color variation" hint="0..1 random brightness / hue per instance">
                <NumberField value={d.colorVariation ?? 0} step={0.05} min={0} max={1} onChange={(colorVariation) => setDetail(i, { colorVariation })} />
              </PropRow>
              <PropRow label="Tint" hint="Multiplied into every instance (dry grass, autumn)">
                <ColorField value={d.tint ?? [1, 1, 1]} alpha={false} onChange={(v) => setDetail(i, { tint: [v[0] ?? 1, v[1] ?? 1, v[2] ?? 1] })} />
              </PropRow>
              <PropRow label="Fade out" hint="Shrink into the ground near the detail distance instead of popping">
                <Toggle checked={d.fade !== false} onChange={(fade) => setDetail(i, { fade })} />
              </PropRow>
              <PropRow label="Cast shadows" hint="Off is faster for grass">
                <Toggle checked={d.castShadows !== false} onChange={(castShadows) => setDetail(i, { castShadows })} />
              </PropRow>
              <PropRow label="Material" hint="Optional *.material.json replacing the mesh material">
                <TextField value={d.material ?? ''} onChange={(v) => setDetail(i, { material: v || undefined })} placeholder="materials/grass.material.json" mono />
              </PropRow>
              {d.map && (
                <div className="tilemap-row">
                  <span className="muted">Painted density map</span>
                  <span className="bar-spacer" />
                  <button type="button" className="small ghost" onClick={() => setDetail(i, { map: undefined })}>
                    Clear paint
                  </button>
                </div>
              )}
            </div>
          ))}
          <datalist id="terrain-meshes">
            {meshes.map((p) => (
              <option key={p} value={p} />
            ))}
            <option value="primitive:grass" />
            <option value="primitive:tallGrass" />
          </datalist>
        </>
      )}

      {!voxel && doc && (
        <>
          <div className="tilemap-row">
            <span className="muted">Neighbours</span>
          </div>
          <NeighborsPanel
            grid={grid}
            self={entity.id}
            linked={linked}
            autoStitch={value.autoStitch !== false}
            onCreate={(side) => void createNeighbor(side)}
            onStitch={() => terrainCommand(entity.id, { op: 'stitch', mode: 'average', width: 4 })}
            onAutoStitch={(on) => {
              const next = { ...value };
              if (on) delete next.autoStitch;
              else next.autoStitch = false;
              onChange(next);
            }}
          />
        </>
      )}

      <div className="tilemap-row">
        <span className="muted">Type</span>
      </div>
      <div className="tilemap-row" style={{ flexWrap: 'wrap', gap: 4 }}>
        <button
          type="button"
          className="small ghost"
          title="Replace the terrain with a new generated voxel world you can dig, tunnel and build in: hills, biomes, soil, rock, caves, coal and a bedrock floor (the layers are replaced with a matching set)"
          onClick={() => void createVoxelWorld()}
        >
          New generated voxel world
        </button>
        <button
          type="button"
          className="small ghost"
          title={
            voxel
              ? 'Replace the terrain with an empty heightmap (layers and details are kept)'
              : 'Replace the terrain with an empty voxel terrain without generator, to build from nothing (layers and details are kept)'
          }
          onClick={() => {
            const next = voxel ? 'heightmap' : 'voxel';
            if (window.confirm(`Replace this terrain with an empty ${next} terrain? (Undo restores it.)`)) terrainCommand(entity.id, { op: 'create', type: next });
          }}
        >
          Make empty {voxel ? 'heightmap' : 'voxel'} terrain
        </button>
      </div>
    </div>
  );
}

/** Drops `undefined` fields (removed settings). */
function clean<T extends Record<string, unknown>>(o: T): T {
  const out = { ...o };
  for (const k of Object.keys(out)) if (out[k] === undefined) delete out[k];
  return out;
}
